import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { WifiOff, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

function formatDuration(ms: number): string {
  const secs = Math.floor(ms / 1_000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}

/**
 * Fixed banner shown when the app loses network connectivity.
 * Sits below the mobile header / status bar, or at the top on desktop.
 *
 * Matches the HUD glass aesthetic: dark glass bg, mono font, uppercase labels.
 */
export function OfflineBanner() {
  const { isOnline, offlineDurationMs } = useNetworkStatus();

  if (isOnline) return null;

  return (
    <div
      className={cn(
        'fixed left-0 right-0 z-50 flex items-center justify-center gap-3 px-4',
        // Mobile: below header (3.5rem) + status bar (1.5rem) + safe area
        'top-[calc(3.5rem+1.5rem+env(safe-area-inset-top,0px))]',
        // Desktop: below sidebar header area
        'lg:top-0',
        'h-8 border-b border-amber-500/20',
      )}
      style={{
        background: 'rgba(30, 20, 8, 0.95)',
        backdropFilter: 'blur(12px)',
      }}
      role="alert"
      aria-live="polite"
    >
      <WifiOff className="h-3 w-3 text-amber-400 flex-shrink-0" strokeWidth={1.8} />
      <span className="text-[9px] font-mono tracking-[0.12em] uppercase text-amber-300">
        Offline
      </span>
      {offlineDurationMs > 5_000 && (
        <>
          <span className="w-px h-3 bg-amber-500/20" />
          <span className="text-[9px] font-mono tracking-[0.10em] text-amber-400/70">
            {formatDuration(offlineDurationMs)}
          </span>
        </>
      )}
      <span className="w-px h-3 bg-amber-500/20" />
      <button
        onClick={() => window.location.reload()}
        className="flex items-center gap-1 text-[9px] font-mono tracking-[0.10em] uppercase text-amber-300/80 hover:text-amber-200 transition-colors"
      >
        <RefreshCw className="h-2.5 w-2.5" strokeWidth={1.8} />
        Retry
      </button>
    </div>
  );
}
